import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { Curve } from '../../../interfaces/toolData';
import type { SidebarFile } from '../../../interfaces/sidebar';
import { SearchBar } from './SearchBar';
import { FileList } from './FileList';

interface CurveExplorerProps {
  /** Lightweight file data - no heavy time series data */
  files: SidebarFile[];
  
  /** Optional callback for curve selection (vestigial - @dnd-kit is preferred) */
  onCurveSelect?: (curve: Curve, fileType: string) => void;
}

/**
 * Simple curve exploration component - handles search, filtering, and curve display
 * 
 * Key Design Principles:
 * 1. Keep it simple - just expand/collapse, search, and drag
 * 2. No unnecessary data transformations
 * 3. Clear state management
 */
export function CurveExplorer({ files, onCurveSelect }: CurveExplorerProps) {
  // Search state (now debounced by SearchBar component)
  const [searchTerm, setSearchTerm] = useState('');
  
  // Expansion state - starts with all files expanded
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  
  // Drag state
  const [draggedCurve, setDraggedCurve] = useState<string | null>(null);

  // Note: Safari may have scroll interference during drag operations in scrollable containers
  // This is a known WebKit limitation - drag-and-drop works correctly in Chrome and Firefox

  // Simple auto-expand on initial load
  useEffect(() => {
    if (files.length > 0) {
      const successfulFileIds = files
        .filter(file => file.status === 'success' || file.status === 'error')
        .map(file => file.id);

      setExpandedFiles(new Set(successfulFileIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length]); // Only depend on file count, not the files array itself to prevent re-expansion

  // Convert files to FileGroup format (simplified)
  const fileGroups = useMemo(() => {
    const groups = files
      .filter(file => file.status === 'success' || file.status === 'error')
      .map(file => ({
        id: file.id,
        fileName: file.filename,
        fileType: file.fileType || null,
        curves: (file.curves || []).map(curve => ({
          name: curve.name,
          unit: curve.yUnit,
          fileType: file.fileType || 'unknown'
        })),
        error: file.error
      }));

    return groups;
  }, [files]);

  // Filter files and curves based on search (searchTerm is already debounced by SearchBar)
  const filteredData = useMemo(() => {
    if (!searchTerm) return fileGroups;
    
    const lowercaseSearch = searchTerm.toLowerCase();
    
    return fileGroups
      .map(fileGroup => {
        // Filter curves within this file
        const matchingCurves = fileGroup.curves.filter(curve =>
          curve.name.toLowerCase().includes(lowercaseSearch)
        );
        
        // Include file if it has matching curves or if filename matches
        const fileNameMatches = fileGroup.fileName.toLowerCase().includes(lowercaseSearch);
        
        if (matchingCurves.length > 0 || fileNameMatches) {
          return {
            ...fileGroup,
            curves: fileNameMatches ? fileGroup.curves : matchingCurves
          };
        }
        
        return null;
      })
      .filter(fileGroup => fileGroup !== null);
  }, [fileGroups, searchTerm]);

  // Calculate totals for display
  const totalCurves = useMemo(() => {
    return fileGroups.reduce((sum, fileGroup) => sum + fileGroup.curves.length, 0);
  }, [fileGroups]);

  const totalFiles = fileGroups.length;

  // Simple event handlers
  const handleToggleExpansion = useCallback((fileId: string) => {
    setExpandedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  }, []);

  const handleDragStart = useCallback((curve: { name: string; unit: string; fileType: string }, fileType: string) => {
    setDraggedCurve(`${fileType}:${curve.name}`);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedCurve(null);
  }, []);

  const handleCurveClick = useCallback((_curve: { name: string; unit: string; fileType: string }, _fileType: string) => {
    // For now, do nothing - @dnd-kit drag is the primary interaction
    // This callback is here for future extension if click actions are needed
  }, []);

  // Empty state
  if (files.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: 'text.secondary',
          p: 3
        }}
      >
        <Typography variant="h6" gutterBottom>
          No Files Loaded
        </Typography>
        <Typography variant="body2">
          Use the File tab to upload data files for curve exploration
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header with search */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Curve Explorer
        </Typography>
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder={`Search ${totalCurves} curves in ${totalFiles} files...`}
        />
      </Box>

      {/* File list */}
      <Box sx={{ 
        flex: 1, 
        // Disable scrolling entirely during drag to prevent interference
        overflow: draggedCurve ? 'hidden' : 'auto',
        // ✅ SAFARI FIX: Prevent WebKit from interpreting drag as scroll
        touchAction: draggedCurve ? 'none' : 'auto',
        overscrollBehavior: 'contain', // Prevent scroll chaining in Safari
        // Prevent scroll during drag to avoid interference
        scrollBehavior: draggedCurve ? 'auto' : 'smooth',
        // Custom scrollbar for better visibility
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '4px'
        }
      }}>
        <FileList
          filteredData={filteredData}
          files={files}
          debouncedSearchTerm={searchTerm}
          totalCurves={totalCurves}
          totalFiles={totalFiles}
          expandedFiles={expandedFiles}
          onToggleExpansion={handleToggleExpansion}
          draggedCurve={draggedCurve}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onCurveClick={handleCurveClick}
          onCurveSelect={onCurveSelect}
        />
      </Box>
    </Box>
  );
}

// Note: SidebarFile and CurveMetadata are now exported from '@/interfaces/sidebar' 